import { BigSwitch, ReactIcons } from "@kalyzee/kalyzee-ui";
import { useTriggerable } from "@kalyzee/kast-web-sdk/web-components";
import { ROLE } from "@kalyzee/kast-web-sdk/http";
import clsx from "clsx";
import { useCallback, useMemo, useRef, useState } from "react";
import { BannerButton } from "../../../../components/common/BannerButton";
import { CreateMeetingPopup } from "../../../../components/common/CreateMeetingPopup";
import { MasterUI } from "../../../../components/common/MasterUI";
import { MediaConfigPopup } from "../../../../components/common/MediaConfigPopup";
import { MediaGlobalControls, MediaSourcePicker } from "../../../../components/common/MediaSourcePicker";
import { PageMasterMode, usePageContextRef } from "../../../../components/navigation/PageContext";
import { AnimatedBackground } from "../../../../components/utils/AnimatedBackground";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { isRoleGreaterThanOrEqual } from "../../../../helpers/roles";
import { useMeetingAccessibility, useMeetingSources } from "../../../../hooks/meeting";
import { useSettings } from "../../../../hooks/settings";
import { MeetingReplayView } from "../vod/MeetingReplayView";
import { EventsAgenda } from "./EventsAgenda";
import { JoinMeetingView } from "./JoinMeetingView";
import styles from "./list.module.css";
import { VodsAgenda } from "./VodsAgenda";
import { getGlobalQueryLabel, NavigateGlobalQuery } from "../../../../helpers/navigation";

type AgendaView = "events" | "vods";

const SWITCH_OPTIONS: { value: AgendaView; label: string }[] = [
  { value: "events", label: "Activités" },
  { value: "vods", label: "Replays" },
];

export const MeetingListStudentPage = () => {
  const pageContextRef = usePageContextRef<SessionMode.MASTER>();
  const sources = useMeetingSources();
  const [settings] = useSettings();
  const disableMeetingList = settings?.ui?.disableMeetingList === true;

  const isMobile = useAppStoreSelector(state => state.isMobile);
  const userRole = useAppStoreSelector(state => state.user?.role);
  const canCreateMeeting = userRole ? isRoleGreaterThanOrEqual(userRole, ROLE.ORGANIZER) : false;

  const hasVisibleSources = useMemo(() => sources.some(s => s.visible), [sources]);

  const logoContainerRef = useRef<HTMLImageElement>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [mediaConfigOpen, setMediaConfigOpen] = useState(false);

  // The `vodId` query drives the Replays tab ("_" sentinel selects the tab)
  // and, with a real meeting id, opens that replay as an overlay above the
  // list — which stays mounted so its filters/results/scroll survive.
  const rawVodId = pageContextRef.current?.searchParams?.get(getGlobalQueryLabel(NavigateGlobalQuery.VOD_ID)) ?? null;
  const view: AgendaView = rawVodId !== null ? "vods" : "events";
  const vodId = rawVodId && rawVodId !== "_" ? rawVodId : undefined;

  // Same data source as the /player route; no-ops while no replay is open.
  const accessibility = useMeetingAccessibility(vodId);

  useTriggerable(logoContainerRef, { clickCount: 3 }, () => pageContextRef.current?.openSettingsPopup());

  const onChangeView = useCallback((value: AgendaView) => {
    pageContextRef.current?.goTo(
      PageMasterMode.LIST,
      undefined,
      { replace: true, queries: { [getGlobalQueryLabel(NavigateGlobalQuery.VOD_ID)]: value === "vods" ? "_" : undefined } },
    );
  }, [pageContextRef]);

  // Close the replay overlay: always land on the Replays tab of the list
  // underneath, via replace. In-app opens push `?vodId=<id>` over `?vodId=_`,
  // so browser Back stays coherent; deep-linked first entries stay inside the
  // app. Not goToPrevious: it rebuilds the previous pathname without its query
  // (losing the tab) and can target a stale non-list page.
  const onCloseVod = useCallback(() => {
    pageContextRef.current?.goTo(
      PageMasterMode.LIST,
      undefined,
      { replace: true, queries: { [getGlobalQueryLabel(NavigateGlobalQuery.VOD_ID)]: "_" } },
    );
  }, [pageContextRef]);

  return (
    <MasterUI onBack={vodId ? onCloseVod : undefined}>
      {vodId && (
        <div className={styles.vodOverlay}>
          <MeetingReplayView accessibility={accessibility} />
        </div>
      )}
      <AnimatedBackground
        className={vodId ? styles.hiddenUnderOverlay : undefined}
        contentClassName={styles.backgroundHome}
      >
        {disableMeetingList ? <JoinMeetingView /> : (
        <div className={styles.mainHomeLogged}>
          <div className={styles.mainHomeLoggedContent}>
            <div className={clsx(styles.layout, !isMobile && !hasVisibleSources && styles.layoutNoMedia)}>
              {!isMobile && hasVisibleSources && (
                <div className={styles.mediaColumn}>
                  <MediaSourcePicker
                    sources={sources}
                    className={styles.mediaSelector}
                    // We can ignore gesture errors
                    // here because this component is only used to configure the sources
                    // it will be mounted again when joining the meeting
                    // and there wont be gesture errors anymore
                    ignoreGestureError
                  />
                  <MediaGlobalControls sources={sources} className={styles.globalControls} />
                </div>
              )}

              <div className={styles.mainColumn}>
                <div className={styles.switchColumn}>
                  <BigSwitch
                    className={styles.bigSwitch}
                    options={SWITCH_OPTIONS}
                    value={view}
                    onChange={onChangeView}
                  />
                </div>

                <div className={styles.eventsColumn}>
                  {view === "events" ? <EventsAgenda /> : <VodsAgenda />}
                </div>

                {!(isMobile && view === "vods") && (
                  <div className={styles.actionButtons}>
                    {isMobile && hasVisibleSources && (
                      <BannerButton
                        icon={<ReactIcons.Settings />}
                        title="Configurer mes médias"
                        subtitle={isMobile ? undefined : "Choisissez caméra et micro avant de rejoindre"}
                        background="linear-gradient(135deg, var(--color-meet-purple-2) 0%, var(--color-meet-purple-1) 100%)"
                        onClick={() => setMediaConfigOpen(true)}
                      />
                    )}
                    {canCreateMeeting && (
                      <BannerButton
                        icon={<ReactIcons.Timer />}
                        title="Créer un meeting instantané"
                        subtitle={isMobile ? undefined : "Démarrez un nouveau cours en quelques secondes"}
                        background="var(--color-element-glass-bg)"
                        style={{
                          color: "var(--color-agenda-title)",
                          ["--banner-icon-circle-bg" as any]: "var(--color-element-banner-icon-circle-bg)",
                        }}
                        onClick={() => setCreateOpen(true)}
                      />
                    )}
                    {!isMobile && !hasVisibleSources && (
                      <MediaGlobalControls sources={sources} className={styles.globalControls} />
                    )}
                  </div>
                )}
              </div>
            </div>

            <CreateMeetingPopup
              visible={createOpen}
              onClose={() => setCreateOpen(false)}
              onCreated={(meeting) => {
                setCreateOpen(false);
                // Pass `openShare` via react-router state so the meeting
                // page knows to auto-open the share popup on arrival.
                pageContextRef.current?.goTo(
                  PageMasterMode.MEETING_DASHBOARD,
                  { id: meeting._id },
                  { state: { openShare: true } },
                );
              }}
            />
            <MediaConfigPopup
              visible={mediaConfigOpen}
              onClose={() => setMediaConfigOpen(false)}
              sources={sources}
            />
          </div>
        </div>
        )}
      </AnimatedBackground>
    </MasterUI>
  );
};

